import { useCallback, useEffect, useMemo } from 'react'

import { CursorState } from '@editor/config/constants/cursor'
import { DrawingParameterMeasurementLabel, DrawingType } from '@editor/interfaces/drawing'
import { EditorConfig } from '@editor/interfaces/editor'
import { BlueprintEditorTools } from '@editor/pages/projects/blueprint-editor/definitions'
import { removeDrawings } from '@editor/pages/projects/blueprint-editor/store/drawings'
import { isDrawingOfType } from '@editor/services/Drawing'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setCursor, setWorkingElementId } from '@editor/store/tool'
import { useSelector } from 'react-redux'

const useEditor = (): EditorConfig => {
  // Store
  const dispatch = useAppDispatch()
  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)
  const prevSelectedTool = useSelector((state: RootState) => state.blueprint.editor.prevSelectedTool)
  const workingElementId = useSelector((state: RootState) => state.toolUtil.core.workingElementId)
  const drawings = useSelector((state: RootState) => state.blueprint.drawings.document.drawings)

  // Derived state
  const isToolSelected = useMemo(() => selectedTool === BlueprintEditorTools.MeasurementLabel, [selectedTool])
  const isPreviousTool = useCallback((tool: BlueprintEditorTools) => prevSelectedTool === tool, [prevSelectedTool])
  const hasWorking = useMemo(
    () =>
      drawings.some(
        (d) =>
          isDrawingOfType<DrawingParameterMeasurementLabel>(d, DrawingType.MeasurementLabel) &&
          d.drawing_parameters.uuid === workingElementId,
      ),
    [drawings, workingElementId],
  )

  /**
   * When user changes tools (in or out of Polyline tool), update cursor.
   */
  useEffect(() => {
    if (isToolSelected) {
      dispatch(setCursor(CursorState.CROSSHAIR))
    } else if (isPreviousTool(BlueprintEditorTools.MeasurementLabel) && selectedTool !== BlueprintEditorTools.Focus) {
      dispatch(setCursor(CursorState.DEFAULT))
      if (hasWorking && workingElementId) {
        dispatch(removeDrawings([workingElementId]))
        dispatch(setWorkingElementId(undefined))
      }
    }
  }, [isToolSelected, selectedTool, hasWorking, workingElementId, isPreviousTool, dispatch])

  return {}
}

export default useEditor
